(() => {
  if (customElements.get('om-delivery-estimate')) return;

  const DAY_MS = 24 * 60 * 60 * 1000;
  const IST_OFFSET_MS = 330 * 60 * 1000;
  const PIN_STORAGE_KEY = 'om_delivery_pincode';

  class OmDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.input = this.querySelector('[data-om-delivery-input]');
      this.checkButton = this.querySelector('[data-om-delivery-check]');
      this.changeButton = this.querySelector('[data-om-delivery-change]');
      this.entry = this.querySelector('[data-om-delivery-entry]');
      this.result = this.querySelector('[data-om-delivery-result]');
      this.promise = this.querySelector('[data-om-delivery-promise]');
      this.locationOutput = this.querySelector('[data-om-delivery-location]');
      this.dateOutput = this.querySelector('[data-om-delivery-date]');
      this.countdownOutput = this.querySelector('[data-om-delivery-countdown]');
      this.festivalOutput = this.querySelector('[data-om-delivery-festival]');
      this.message = this.querySelector('[data-om-delivery-message]');
      this.disclaimer = this.querySelector('[data-om-delivery-disclaimer]');

      if (!this.input || !this.checkButton) return;

      this.config = this.readConfig();
      this.activeEstimate = null;
      this.boundCheck = () => this.checkPincode();
      this.boundChange = () => this.changeLocation();
      this.boundInput = () => this.normalizeInput();
      this.boundKeydown = (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.checkPincode();
        }
      };

      this.checkButton.addEventListener('click', this.boundCheck);
      this.changeButton?.addEventListener('click', this.boundChange);
      this.input.addEventListener('input', this.boundInput);
      this.input.addEventListener('keydown', this.boundKeydown);

      try {
        const savedPincode = window.localStorage.getItem(PIN_STORAGE_KEY);
        if (/^\d{6}$/.test(savedPincode || '')) {
          this.input.value = savedPincode;
          this.activeEstimate = this.buildLocalEstimate(savedPincode);
          this.renderEstimate();
        }
      } catch (_error) {
        // Storage can be blocked without affecting the estimator.
      }
    }

    disconnectedCallback() {
      window.clearInterval(this.timerId);
      this.checkButton?.removeEventListener('click', this.boundCheck);
      this.changeButton?.removeEventListener('click', this.boundChange);
      this.input?.removeEventListener('input', this.boundInput);
      this.input?.removeEventListener('keydown', this.boundKeydown);
      this.abortController?.abort();
    }

    readConfig() {
      return {
        endpoint: (this.dataset.apiEndpoint || '').trim(),
        originPincode: (this.dataset.originPincode || '281001').trim(),
        cutoffHour: this.clampInteger(this.dataset.cutoffHour, 12, 0, 23),
        localDays: this.clampInteger(this.dataset.localTransitDays, 2, 1, 14),
        expressDays: this.clampInteger(this.dataset.expressTransitDays, 3, 1, 14),
        majorDays: this.clampInteger(this.dataset.majorTransitDays, 4, 1, 14),
        standardDays: this.clampInteger(this.dataset.standardTransitDays, 5, 1, 14),
        remoteDays: this.clampInteger(this.dataset.remoteTransitDays, 6, 1, 14),
        localPincodes: this.parsePincodeRules(this.dataset.localPincodes),
        expressPincodes: this.parsePincodeRules(this.dataset.expressPincodes),
        majorPincodes: this.parsePincodeRules(this.dataset.majorPincodes),
        remotePincodes: this.parsePincodeRules(this.dataset.remotePincodes),
        localFallback: this.dataset.localFallback !== 'false',
        festivalEnabled: this.dataset.festivalEnabled === 'true',
        festivalName: (this.dataset.festivalName || '').trim(),
        festivalDate: (this.dataset.festivalDate || '').trim(),
        productId: this.dataset.productId || '',
      };
    }

    clampInteger(value, fallback, min, max) {
      const parsed = Number.parseInt(value, 10);
      if (!Number.isFinite(parsed)) return fallback;
      return Math.min(max, Math.max(min, parsed));
    }

    parsePincodeRules(value = '') {
      return value
        .split(/[\s,]+/)
        .map((item) => item.trim())
        .filter((item) => /^\d{1,6}\*?$/.test(item));
    }

    matchesRule(pincode, rules) {
      return rules.some((rule) => {
        if (rule.endsWith('*')) return pincode.startsWith(rule.slice(0, -1));
        return pincode === rule;
      });
    }

    normalizeInput() {
      const normalized = this.input.value.replace(/\D/g, '').slice(0, 6);
      if (this.input.value !== normalized) this.input.value = normalized;
      this.setMessage('');
    }

    async checkPincode() {
      const pincode = this.input.value.replace(/\D/g, '').slice(0, 6);
      this.input.value = pincode;

      if (!/^\d{6}$/.test(pincode)) {
        this.setMessage('Please enter a valid 6-digit PIN code.', true);
        this.input.focus();
        return;
      }

      this.setLoading(true);
      this.setMessage('Checking delivery date…');

      let estimate;
      try {
        estimate = await this.requestEstimate(pincode);
      } catch (_error) {
        if (!this.config.localFallback) {
          this.setLoading(false);
          this.setMessage('Delivery date is temporarily unavailable. Please try again shortly.', true);
          return;
        }
        estimate = this.buildLocalEstimate(pincode);
      }

      this.setLoading(false);

      if (estimate.serviceable === false) {
        this.activeEstimate = null;
        this.promise.hidden = true;
        if (this.disclaimer) this.disclaimer.hidden = true;
        this.setMessage('Delivery is not currently available for this PIN code.', true);
        return;
      }

      this.activeEstimate = estimate;
      this.renderEstimate();

      try {
        window.localStorage.setItem(PIN_STORAGE_KEY, pincode);
      } catch (_error) {
        // Storage is a convenience only.
      }
    }

    async requestEstimate(pincode) {
      if (!this.config.endpoint) return this.buildLocalEstimate(pincode);

      this.abortController?.abort();
      this.abortController = new AbortController();
      const timeoutId = window.setTimeout(() => this.abortController.abort(), 2800);

      try {
        const response = await fetch(this.config.endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            pincode,
            origin_pincode: this.config.originPincode,
            product_id: this.config.productId,
          }),
          signal: this.abortController.signal,
        });

        if (!response.ok) throw new Error(`Delivery endpoint returned ${response.status}`);

        const payload = await response.json();
        const data = payload?.data || payload;
        if (!data || typeof data !== 'object') throw new Error('Invalid delivery response');

        if (data.serviceable === false) return { serviceable: false, pincode };

        const transitDays = this.clampInteger(
          data.transit_days ?? data.transitDays,
          this.getLocalTransitDays(pincode),
          1,
          14,
        );

        return {
          serviceable: true,
          pincode,
          transitDays,
          deliveryDate: this.parseApiDate(data.delivery_date || data.deliveryDate),
          location: this.buildLocationLabel(data, pincode),
          source: 'api',
        };
      } finally {
        window.clearTimeout(timeoutId);
      }
    }

    buildLocalEstimate(pincode) {
      return {
        serviceable: true,
        pincode,
        transitDays: this.getLocalTransitDays(pincode),
        deliveryDate: null,
        location: `PIN ${pincode}`,
        source: 'local',
      };
    }

    getLocalTransitDays(pincode) {
      if (this.matchesRule(pincode, this.config.remotePincodes)) return this.config.remoteDays;
      if (this.matchesRule(pincode, this.config.localPincodes)) return this.config.localDays;
      if (this.matchesRule(pincode, this.config.expressPincodes)) return this.config.expressDays;
      if (this.matchesRule(pincode, this.config.majorPincodes)) return this.config.majorDays;
      return this.config.standardDays;
    }

    buildLocationLabel(data, pincode) {
      const city = String(data.city || data.district || data.location || '').trim();
      const state = String(data.state || '').trim();
      const place = [city, state].filter(Boolean).join(', ');
      return place ? `${place} ${pincode}` : `PIN ${pincode}`;
    }

    parseApiDate(value) {
      if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return null;
      const [year, month, day] = String(value).split('-').map(Number);
      const date = new Date(Date.UTC(year, month - 1, day));
      return Number.isNaN(date.getTime()) ? null : date;
    }

    renderEstimate() {
      if (!this.activeEstimate) return;

      this.entry.hidden = true;
      this.result.hidden = false;
      this.promise.hidden = false;
      if (this.disclaimer) this.disclaimer.hidden = false;
      this.locationOutput.textContent = this.activeEstimate.location;
      this.setMessage('');

      this.updatePromise();
      window.clearInterval(this.timerId);
      this.timerId = window.setInterval(() => this.updatePromise(), 1000);
    }

    updatePromise() {
      if (!this.activeEstimate) return;

      const timing = this.getDispatchTiming();
      const festivalBuffer = this.getFestivalBuffer();
      const baseDeliveryDate =
        this.activeEstimate.deliveryDate ||
        new Date(timing.dispatchDate.getTime() + this.activeEstimate.transitDays * DAY_MS);
      const deliveryDate = new Date(baseDeliveryDate.getTime() + festivalBuffer * DAY_MS);

      this.countdownOutput.textContent = this.formatCountdown(timing.msUntilCutoff);
      this.dateOutput.textContent = this.formatDate(deliveryDate);
      this.renderFestival(deliveryDate);
    }

    getDispatchTiming() {
      const nowMs = Date.now();
      const istNow = new Date(nowMs + IST_OFFSET_MS);
      const year = istNow.getUTCFullYear();
      const month = istNow.getUTCMonth();
      const day = istNow.getUTCDate();
      const cutoffTodayMs =
        Date.UTC(year, month, day, this.config.cutoffHour, 0, 0) - IST_OFFSET_MS;
      const beforeCutoff = nowMs < cutoffTodayMs;
      const cutoffMs = beforeCutoff ? cutoffTodayMs : cutoffTodayMs + DAY_MS;
      const dispatchDate = new Date(Date.UTC(year, month, day) + (beforeCutoff ? 0 : DAY_MS));

      return {
        dispatchDate,
        msUntilCutoff: Math.max(0, cutoffMs - nowMs),
      };
    }

    getFestivalBuffer() {
      const festivalDate = this.parseApiDate(this.config.festivalDate);
      return this.config.festivalEnabled && this.config.festivalName && festivalDate ? 1 : 0;
    }

    renderFestival(deliveryDate) {
      const festivalDate = this.parseApiDate(this.config.festivalDate);
      const canShow =
        this.config.festivalEnabled && this.config.festivalName && festivalDate && this.festivalOutput;

      if (!canShow) {
        if (this.festivalOutput) this.festivalOutput.hidden = true;
        return;
      }

      const daysBefore = Math.round((festivalDate.getTime() - deliveryDate.getTime()) / DAY_MS);
      this.festivalOutput.classList.toggle('om-delivery-estimate__festival--warning', daysBefore < 0);

      if (daysBefore > 0) {
        this.festivalOutput.textContent = `${daysBefore} ${daysBefore === 1 ? 'day' : 'days'} before ${this.config.festivalName}`;
      } else if (daysBefore === 0) {
        this.festivalOutput.textContent = `Delivery by ${this.config.festivalName}`;
      } else {
        this.festivalOutput.textContent = `Delivery date is after ${this.config.festivalName}`;
      }

      this.festivalOutput.hidden = false;
    }

    formatCountdown(milliseconds) {
      const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      return `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
    }

    formatDate(date) {
      return new Intl.DateTimeFormat('en-IN', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: 'UTC',
      }).format(date);
    }

    changeLocation() {
      this.activeEstimate = null;
      window.clearInterval(this.timerId);
      this.promise.hidden = true;
      this.result.hidden = true;
      this.entry.hidden = false;
      if (this.disclaimer) this.disclaimer.hidden = true;
      this.setMessage('');

      try {
        window.localStorage.removeItem(PIN_STORAGE_KEY);
      } catch (_error) {
        // Storage can be blocked without affecting the estimator.
      }

      this.input.focus();
      this.input.select();
    }

    setLoading(isLoading) {
      this.checkButton.disabled = isLoading;
      this.checkButton.textContent = isLoading ? 'Checking…' : 'Check';
      this.setAttribute('aria-busy', String(isLoading));
    }

    setMessage(text, isError = false) {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.classList.toggle('om-delivery-estimate__message--error', isError);
    }
  }

  customElements.define('om-delivery-estimate', OmDeliveryEstimate);
})();
